import { useMutation } from '@apollo/client'
import { getDefaultCompletes } from '../../controls/ProjectTypesPanel/ProjectTypesPanel.utils'
import { ProjectType } from '../../data/gql-gen/dashboard/graphql'
import { CREATE_QUESTIONNAIRE } from '../../data/gql/questionnaire/mutations/createQuestionnaire'
import { LoggerErrorType } from '../../data/logger'
import { captureApolloError } from '../../utils/HelperFunctions'
import { PanelSupplier } from '../../utils/audienceHelper'
import { useCreateFieldworkV2 } from '../audience/createOrUpdateFieldworkV2'

export interface InitialQuestionnaireInput {
  projectId: string
  surveyId: string
  projectType: ProjectType
  accountMarkup: number
  waveId?: string
  countryCode?: string
  countryLanguage?: string
  samplesNeeded?: number
}

/**
 * Creates the questionnaire for a project's first survey, then that survey's
 * default fieldwork. Resolves to whether both were created.
 */
export const useCreateInitialQuestionnaire = () => {
  const { createFieldworkV2 } = useCreateFieldworkV2()
  const [createQuestionnaire] = useMutation(CREATE_QUESTIONNAIRE, {
    context: { clientName: 'questionnaire' },
    onError: (error) => {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'createQuestionnaire',
        error
      )
    }
  })

  const createInitialQuestionnaire = async ({
    projectId,
    surveyId,
    projectType,
    accountMarkup,
    waveId,
    countryCode,
    countryLanguage,
    samplesNeeded
  }: InitialQuestionnaireInput): Promise<boolean> => {
    const isFdChat = projectType === ProjectType.FdChat

    const questionnaire = await createQuestionnaire({
      variables: { projectId, surveyId, waveId }
    })

    const fieldwork = await createFieldworkV2({
      variables: {
        input: {
          surveyId,
          countryCode: countryCode || 'GB',
          countryLanguage: countryLanguage || 'en',
          samplesNeeded: samplesNeeded ?? getDefaultCompletes(projectType),
          accountMarkup,
          panelSupplierCode: isFdChat
            ? PanelSupplier.PROLIFIC
            : PanelSupplier.PURESPECTRUM,
          audienceDescription: isFdChat ? '' : undefined
        }
      }
    })

    return Boolean(
      questionnaire.data?.createQuestionnaireV2 &&
        fieldwork.data?.createFieldworkV2
    )
  }

  return { createInitialQuestionnaire }
}
